import { MessageType } from '@/types/chat.type'

const PLACEHOLDER_CAPTIONS = new Set(['sent a file', 'sent an image', 'sent a video'])
const IMAGE_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.heic', '.heif']

export const isImageAttachmentMessage = (
  type: MessageType,
  fileName?: string,
  fileType?: string
): boolean => {
  if (type === MessageType.IMAGE) return true
  if (fileType?.startsWith('image/')) return true
  if (!fileName) return false

  const lowerFileName = fileName.toLowerCase()
  return IMAGE_FILE_EXTENSIONS.some(extension => lowerFileName.endsWith(extension))
}

export const getPlainMessageText = (content: string): string => {
  if (typeof document === 'undefined') {
    return content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  }

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = content || ''
  return (tempDiv.textContent || tempDiv.innerText || '').trim()
}

export const isRedundantAttachmentCaption = (content: string, fileName?: string): boolean => {
  if (!fileName) return false
  const plainText = getPlainMessageText(content)
  return plainText.toLowerCase() === fileName.toLowerCase()
}

export const isPlaceholderCaption = (content: string, fileName?: string): boolean => {
  const plainText = getPlainMessageText(content)
  if (!plainText) return true
  if (PLACEHOLDER_CAPTIONS.has(plainText.toLowerCase())) return true
  return isRedundantAttachmentCaption(content, fileName)
}

export const isAttachmentMessageType = (type: MessageType): boolean =>
  type === MessageType.FILE || type === MessageType.IMAGE || type === MessageType.VIDEO

export const hasMessageCaption = (content: string, fileName?: string): boolean =>
  !isPlaceholderCaption(content, fileName)

export const hasAttachmentWithCaption = (type: MessageType, content: string, fileName?: string): boolean =>
  isAttachmentMessageType(type) && hasMessageCaption(content, fileName)

export const getAttachmentPlaceholderContent = (file: File): string => {
  if (file.type.startsWith('image/')) return 'Sent an image'
  if (file.type.startsWith('video/')) return 'Sent a video'
  return 'Sent a file'
}

export const hasUserMessageText = (content: string): boolean => {
  const plainText = getPlainMessageText(content)
  return plainText.length > 0 && !isPlaceholderCaption(content)
}
